<script setup>

import {computed, onMounted, ref} from "vue";

const props = defineProps({
    courseId: {
        type: Number,
        required: true
    }
})

const course = ref(null)

const loadCourse = async () => {
    const res = await axios.get(route('api.course.customerarea.show', props.courseId))
    course.value = res.data.course
}

const handleChapterCompletion = (chapter) => {
    const courseChapters = course.value.chapters
    const chapterIndex = courseChapters.findIndex(c => c.id === chapter.id)
    if (chapterIndex !== -1) {
        course.value.chapters[chapterIndex].completed = chapter.completed
    }
}

const displayRows = computed(() => {
    if (!course.value) {
        return [];
    }

    const chaptersById = new Map((course.value.chapters ?? []).map((chapter) => [chapter.id, chapter]));
    const groupsById = new Map((course.value.chapter_groups ?? []).map((group) => [group.id, group]));
    const structure = (course.value.chapter_structure?.length ?? 0) > 0
        ? course.value.chapter_structure
        : (course.value.chapters ?? []).map((chapter) => ({ type: 'chapter', chapter_id: chapter.id }));
    const requiresSequentialUnlock = course.value.type === 'class' && course.value.chapters_must_be_consumed_in_order;
    let previousChapterCanUnlockNext = true;

    return structure.map((item) => {
        if (item.type === 'group') {
            const group = groupsById.get(item.group_id);
            if (!group) {
                return null;
            }

            return {
                kind: 'group',
                id: group.id,
                title: group.title,
            };
        }

        const chapter = chaptersById.get(item.chapter_id);
        if (!chapter) {
            return null;
        }

        const available = requiresSequentialUnlock ? previousChapterCanUnlockNext : chapter.available;
        previousChapterCanUnlockNext = available && chapter.completed;

        return {
            kind: 'chapter',
            id: chapter.id,
            chapter,
            available,
        };
    }).filter((row) => row !== null);
});

onMounted(() => {
    loadCourse()
})

</script>
<template>
    <div
        v-if="course"
        class="flex items-stretch min-h-screen"
        :class="'course-id-' + courseId"
    >
        <course-chapter-navigation-modal
            :course-id="courseId"
            @chapter-completion-update="handleChapterCompletion"
            class="lg:mr-4"
        ></course-chapter-navigation-modal>
        <div class="w-full max-w-5xl mx-auto mt-4">
            <h1>{{ course.title }}</h1>
            <div v-if="course.description"
                 v-html="course.description"
            ></div>

            <ul class="grid items-stretch sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                <li
                    v-for="row in displayRows"
                    :key="`${row.kind}-${row.id}`"
                    :class="row.kind === 'group' ? 'sm:col-span-2 lg:col-span-3' : ''"
                >
                    <div v-if="row.kind === 'group'" class="border-t pt-2">
                        <h2 class="text-base font-semibold text-gray-700">{{ row.title }}</h2>
                    </div>
                    <card-basic v-else :compact="true" margin-bottom-class="mb-0" class="min-h-full">
                        <div
                            v-if="row.chapter.thumbnail_url"
                            class="mb-2 -mx-4 -mt-4"
                        >
                            <img
                                :src="row.chapter.thumbnail_url"
                                class="aspect-video object-contain "
                                alt=""
                            />
                        </div>
                        <div class="leading-5">
                            <a
                                v-if="row.chapter.published && row.available"
                                :href="route('course.customerarea.chapter.show', [course.id, row.chapter.id])"
                                class="text-inherit text-xs mf-link-immune"
                                :class="row.chapter.id === currentChapterId ? 'font-bold' : ''"
                            >
                                <div class="font-bold text-sm">{{ row.chapter.title }}</div>
                                <div
                                    v-if="row.chapter.description"
                                    class="mt-1 w-full whitespace-pre-wrap"
                                >
                                    {{ row.chapter.description }}
                                </div>
                                <span
                                    v-if="row.chapter.id === currentChapterId"
                                    class="absolute right-full top-1 bottom-1 bg-wlPrimary w-1 -translate-x-1 rounded-wl"
                                ></span>
                            </a>
                            <span
                                v-else
                                class="text-gray-400 cursor-not-allowed text-xs"
                            >
                                <div class="font-bold text-sm">{{ row.chapter.title }}</div>
                                <div
                                    v-if="row.chapter.description"
                                    class="mt-1 w-full whitespace-pre-wrap"
                                >
                                    {{ row.chapter.description }}
                                </div>
                            </span>
                            <div class="border-t flex justify-between items-center mt-2 pt-2">
                                <div>
                                    <span
                                        v-if="course.has_multiple_providers && row.chapter.provider"
                                        class="text-gray-400 text-xs"
                                    >{{ _mft('shared:by') }} {{ row.chapter.provider.name }}</span>
                                </div>
                                <span v-if="row.chapter.completed" class="flex items-center">
                                    <span
                                        class="inline-block w-4 h-4 relative"
                                    >
                                        <span
                                            class="border-2 absolute transition-all rounded-full bg-myflowGreen border-myflowGreen -inset-px opacity-70"
                                        >
                                            <span
                                                class="flex items-center justify-center absolute inset-0 transition-all duration-75"
                                            >
                                                <i class="fa-solid fa-check text-white m-auto" style="font-size: 10px"></i>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="text-xs text-gray-400 ml-1">{{ _mft('shared:done') }}</span>
                                </span>
                            </div>
                        </div>
                    </card-basic>
                </li>
            </ul>
        </div>


    </div>
    <div v-else class="flex justify-center mt-24">
        <mf-spinner-medium class="w-24 h-12" />
    </div>
</template>
